"use client";

import { ArrowDown, Compass } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Blueprint() {
  const milestones = [{
    marker: "S-01",
    title: "Discovery Audit",
    description: "We map your existing operational bottlenecks. Our analysts identify high-friction manual tasks that are prime candidates for intelligent automation.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_discovery_audit.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_discovery_audit.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_discovery_audit.png-wvc-sizes",
    alt: "Consultant reviewing digital workflow diagrams"
  }, {
    marker: "S-02",
    title: "Architecture Design",
    description: "We build a custom logic framework. By integrating your CRM, email, and support tools, we create a seamless data flow that eliminates human error.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_architecture_design.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_architecture_design.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_architecture_design.png-wvc-sizes",
    alt: "Digital blueprint of automated system architecture"
  }, {
    marker: "S-03",
    title: "AI Integration",
    description: "Deploying specialized AI agents to handle customer inquiries and scheduling. Your systems begin to learn and adapt to your business rhythm.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_ai_integration.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_ai_integration.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_ai_integration.webp-wvc-sizes",
    alt: "AI neural network visualization on screen"
  }, {
    marker: "S-04",
    title: "System Stress-Test",
    description: "Before full launch, we simulate high-volume traffic to ensure your new automated workflows remain stable, secure, and lightning-fast.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_system_testing.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_system_testing.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_system_testing.png-wvc-sizes",
    alt: "Technical dashboard showing system performance metrics"
  }, {
    marker: "S-05",
    title: "Deployment & Scale",
    description: "The system goes live. We monitor real-time performance, providing ongoing optimization to ensure your business scales without increasing overhead.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_deployment_scale.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_deployment_scale.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_deployment_scale.png-wvc-sizes",
    alt: "Business growth chart showing efficiency gains"
  }, {
    marker: "S-06",
    title: "Continuous Optimization",
    description: "Automation is never static. We provide monthly reviews to refine your AI agents, ensuring they stay ahead of your evolving business needs.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_continuous_optimization.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_continuous_optimization.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1714695/img/automation_continuous_optimization.webp-wvc-sizes",
    alt: "Team reviewing automated performance reports"
  }];
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.3
  });
  const {
    ref: footerRef,
    isVisible: footerVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.5
  });
  return <section data-section-id="3114" id="our-5-step-method" className="bg-background text-foreground py-32 md:py-[200px] px-6 md:px-12 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        {}
        <div ref={headerRef} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 mb-24 md:mb-32">
          <div className={`lg:col-span-6 transition-all duration-1000 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="font-mono text-xs tracking-[0.25em] uppercase mb-8 text-chart-1">
              § 0X — Methodology
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-[76px] leading-[1.05] tracking-tight text-foreground">
              The <span className="text-chart-1 font-mono">Autolexa</span>
              <br />
              <span className="italic font-light text-chart-4">
                automation
              </span>{" "}
              blueprint.
            </h2>
          </div>
          <div className={`lg:col-span-5 lg:col-start-8 flex items-end transition-all duration-1000 delay-200 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <p className="text-base md:text-lg leading-relaxed text-muted-foreground">
              Efficiency is not an accident; it is an architecture. At AutolexaAi, we replace manual friction with intelligent, automated systems. Our five-step methodology ensures your business operations are precise, scalable, and ready for the future of work.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2 bg-border" aria-hidden="true">
            <div className="absolute inset-0 animate-pulse bg-chart-1 opacity-40" />
          </div>

          {}
          <div className="space-y-24 md:space-y-32">
            {milestones.map((milestone, i) => {
            const isOdd = i % 2 === 0;
            return <TimelineMilestone key={i} data-index={i} milestone={milestone} index={i} isLeft={isOdd} />;
          })}
          </div>

          {}
          <div ref={footerRef} className={`relative mt-24 md:mt-32 flex flex-col items-center transition-all duration-1000 ease-out ${footerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            <div className="relative z-10 flex items-center justify-center w-16 h-16 rounded-lg bg-background border border-chart-1 shadow-lg">
              <Compass className="w-7 h-7 text-chart-1" strokeWidth={1.5} />
            </div>
            <div className="mt-6 flex items-center gap-3">
              <p className="font-mono text-xs tracking-[0.3em] uppercase text-chart-1">
                Begin Automation
              </p>
              <ArrowDown className="w-3.5 h-3.5 text-chart-1" strokeWidth={1.5} />
            </div>
          </div>
        </div>
      </div>
    </section>;
}
type Milestone = {
  marker: string;
  title: string;
  description: string;
  image: string;
  srcset: string;
  sizes: string;
  alt: string;
};
function TimelineMilestone({
  milestone,
  index,
  isLeft,
  ...rest
}: {
  milestone: Milestone;
  index: number;
  isLeft: boolean;
  "data-index"?: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  return <div ref={ref} data-index={rest["data-index"] ?? index} className="relative">
      {}
      <div className="absolute left-8 md:left-1/2 top-2 md:-translate-x-1/2 z-10" aria-hidden="true">
        <div className="relative">
          <div className={`w-3.5 h-3.5 rounded-lg transition-all duration-700 ${isVisible ? "scale-100" : "scale-50"}`} style={{
          backgroundColor: "var(--chart-1)",
          boxShadow: isVisible ? `0 0 0 4px var(--accent), 0 0 20px var(--chart-1)` : "none"
        }} />
          {isVisible && <div className="absolute inset-0 rounded-lg animate-ping bg-chart-1 opacity-30" />}
        </div>
      </div>

      {}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0">
        {}
        <div className={`pl-20 md:pl-0 md:pr-20 ${isLeft ? "md:col-start-1 md:text-right" : "md:col-start-2 md:hidden"}`}>
          {isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={true} />}
        </div>

        {}
        <div className={`pl-20 md:pl-20 md:pr-0 ${!isLeft ? "md:col-start-2" : "md:col-start-2 md:hidden"}`}>
          {!isLeft && <MilestoneContent milestone={milestone} isVisible={isVisible} alignRight={false} />}
        </div>
      </div>
    </div>;
}
function MilestoneContent({
  milestone,
  isVisible,
  alignRight
}: {
  milestone: Milestone;
  isVisible: boolean;
  alignRight: boolean;
}) {
  return <div className={`transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-x-0" : alignRight ? "opacity-0 -translate-x-8" : "opacity-0 translate-x-8"}`}>
      <p className="font-mono italic text-3xl md:text-4xl mb-3 text-chart-1">
        {milestone.marker}
      </p>
      <h3 className="font-default text-2xl md:text-[32px] leading-tight mb-4 text-foreground">
        {milestone.title}
      </h3>
      <p className="text-base leading-relaxed mb-6 max-w-md text-muted-foreground" style={{
      marginLeft: alignRight ? "auto" : 0
    }}>
        {milestone.description}
      </p>
      <div className={`overflow-hidden rounded-lg ${alignRight ? "md:ml-auto" : ""}`} style={{
      width: "100%",
      maxWidth: "320px",
      aspectRatio: "4 / 3",
      border: `1px solid var(--border)`
    }}>
        <img src={milestone.image} data-wvc-srcset={milestone.srcset} data-wvc-sizes={milestone.sizes} alt={milestone.alt} loading="lazy" decoding="async" className="w-full h-full object-cover" />
      </div>
    </div>;
}